import CustomButton from '@/components/atoms/CustomButton';
import ConfirmationModal from '@/components/molecules/ConfirmationModal';
import RadioButtonGroup from '@/components/molecules/RadioButtonGroup';
import AppPageTemplate from '@/components/templates/AppPageTemplate';
import { NOTIFICATION_CHANNELS, NOTIFICATION_CHANNELS_SETTINGS_TITLE_AND_DESCRIPTION } from '@/constants';
import { useAuthContext } from '@/context/auth/useAuthContext';
import { useDeactivateOwnAccount } from '@/hooks/auth/deactivateOwnAccount';
import { useNotificationPreferences } from '@/hooks/user/useNotificationPreferences';
import useUpdateNotificationPreferences from '@/hooks/user/useUpdateNotificationPreferences';
import appMessages from '@/messages';
import { TNotificationChannel } from '@/types/Notification.types';
import { Center, HStack, Text, useDisclosure, VStack } from '@chakra-ui/react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';

type TNotificationSetting = 'On' | 'Off';

const Settings = () => {
  const { signOut } = useAuthContext();
  const navigate = useNavigate();

  const onDeactivateAccountSuccess = () => {
    signOut();
    navigate({
      to: '/sign-in',
    });
  };

  const { mutate, isPending: isAccountDeactivationPending } = useDeactivateOwnAccount(onDeactivateAccountSuccess);

  const {
    data: notificationPreferences,
    refetch: refetchNotificationPreferences,
    isLoading,
  } = useNotificationPreferences();

  useEffect(() => {
    if (notificationPreferences) setNewNotificationPreferences(notificationPreferences);
  }, [notificationPreferences]);

  const [newNotificationPreferences, setNewNotificationPreferences] = useState<TNotificationChannel[]>([]);

  const { mutate: updateNotificationPreferences, isPending: isNotificationSettingsUpdatePending } =
    useUpdateNotificationPreferences(refetchNotificationPreferences);

  // TODO: will be added later
  // const {
  //   isOpen: isUpdatePasswordModalOpen,
  //   onOpen: openUpdatePasswordModal,
  //   onClose: closeUpdatePasswordModal,
  // } = useDisclosure();

  const {
    isOpen: isDeactivateAccountModalOpen,
    onOpen: onDeactivateAccountModalOpen,
    onClose: onDeactivateAccountModalClose,
  } = useDisclosure();

  const deactivateUserAccount = () => {
    mutate();
    onDeactivateAccountModalClose();
  };

  const notificationSettingsChanged = useMemo(
    () => newNotificationPreferences.sort().toString() !== notificationPreferences?.sort().toString(),
    [notificationPreferences, newNotificationPreferences],
  );

  const onNotificationSettingChange = useCallback(
    (updatedChannel: TNotificationChannel, newValue: TNotificationSetting) => {
      const updatedNotificationPreferences: TNotificationChannel[] = [...(newNotificationPreferences ?? [])];
      if (newValue === 'On') {
        if (!updatedNotificationPreferences.includes(updatedChannel)) {
          updatedNotificationPreferences.push(updatedChannel);
        }
      } else {
        updatedNotificationPreferences.splice(
          updatedNotificationPreferences.findIndex((notificationChannel) => notificationChannel === updatedChannel),
          1,
        );
      }

      setNewNotificationPreferences(updatedNotificationPreferences);
    },
    [newNotificationPreferences],
  );

  const onConfirmNotificationSettingsUpdate = useCallback(
    () => updateNotificationPreferences(newNotificationPreferences),
    [newNotificationPreferences],
  );

  return (
    <AppPageTemplate>
      {/* Notifications */}
      <VStack gap={'20px'} marginBottom={'80px'}>
        <VStack gap={0}>
          <Text variant={'loraSmallTitle'}>Notifications</Text>
          <Text variant={'urbanistSemiBold'} marginY={'14px'} color={'text.mediumGray'}>
            Select which notifications you want to receive and how.
          </Text>
        </VStack>

        <VStack width={'620px'} gap={'40px'}>
          {NOTIFICATION_CHANNELS.map((notificationChannel) => (
            <NotificationSettingsDisplayRow
              key={notificationChannel}
              channel={notificationChannel}
              value={notificationPreferences?.includes(notificationChannel) ? 'On' : 'Off'}
              onChange={onNotificationSettingChange}
              disabled={isLoading}
            />
          ))}
        </VStack>

        <Center marginTop={'20px'}>
          <CustomButton
            backgroundColor="primary.500"
            labelColor="extra.white"
            label={'Confirm Update'}
            onClick={onConfirmNotificationSettingsUpdate}
            width={'210px'}
            height={'55px'}
            labelStyle={{ fontWeight: 700 }}
            disabled={!notificationSettingsChanged || !notificationPreferences}
            isLoading={isNotificationSettingsUpdatePending}
          />
        </Center>
      </VStack>

      {/* Account Preferences */}
      <VStack gap={'40px'}>
        <VStack gap={0}>
          <Text variant={'loraSmallTitle'}>Account Preferences</Text>
          <Text variant={'urbanistSemiBold'} marginY={'14px'} color={'text.mediumGray'}>
            Take control of your account’s preferences.
          </Text>
        </VStack>
        {/* TODO: update password */}
        {/* <HStack width={'100%'} justifyContent={'space-between'}>
          <VStack align={'start'}>
            <Text variant={'urbanistBoldLarge'} color={'text.darkBlue'}>
              Update Password
            </Text>

            <Text variant={'urbanistSemiBold'} color={'text.mediumGray'}>
              If you want to change your password
            </Text>
          </VStack>
          <VStack>
            <CustomButton
              backgroundColor="extra.white"
              labelColor="text.mediumBlue"
              labelHoverColor="text.darkBlue"
              label={'Update Password'}
              // onClick={onUpdatePassword}
              width={'210px'}
              height={'55px'}
              labelStyle={{ fontWeight: 700 }}
              isLoading={isPending}
            />
          </VStack>
        </HStack> */}
        <HStack width={'100%'} justifyContent={'space-between'}>
          <VStack align={'start'}>
            <Text variant={'urbanistBoldLarge'} color={'text.darkBlue'}>
              Deactivate MyOmnia Account
            </Text>

            <Text variant={'urbanistSemiBold'} color={'text.mediumGray'}>
              If you want to completely close your account
            </Text>
          </VStack>
          <VStack>
            <CustomButton
              backgroundColor="extra.white"
              labelColor="extra.red"
              labelHoverColor="extra.lightRed"
              label={'Deactivate Account'}
              onClick={onDeactivateAccountModalOpen}
              width={'210px'}
              height={'55px'}
              labelStyle={{ fontWeight: 700 }}
              isLoading={isAccountDeactivationPending}
            />
          </VStack>
        </HStack>
        <HStack minWidth={'700px'} justifyContent={'center'} marginTop={'50px'}>
          {/* TODO: will be added later */}
          {/* <VStack>
            <Image
              alt="lock-password"
              src={iconsPng.lockPassword}
              width={140}
              height={140}
              style={{ margin: '30px 0px' }}
            />
            <CustomButton
              disabled
              backgroundColor="primary.500"
              label={appMessages.updatePassword}
              width={FORM_FIELD_WIDTH}
              height={'55px'}
            />
          </VStack> */}
          <ConfirmationModal
            confirmButtonText="Deactivate"
            description={appMessages.deactivateAccountDescription}
            isOpen={isDeactivateAccountModalOpen}
            onClose={onDeactivateAccountModalClose}
            onConfirm={deactivateUserAccount}
            title={appMessages.deactivateAccountConfirm}
            confirmButtonColor="extra.red"
          />
        </HStack>
      </VStack>
    </AppPageTemplate>
  );
};

export const Route = createFileRoute('/_authenticated/settings/')({
  component: Settings,
});

const NotificationSettingsDisplayRow = ({
  channel,
  value,
  onChange,
  disabled,
}: {
  channel: TNotificationChannel;
  value: TNotificationSetting;
  onChange: (channel: TNotificationChannel, newValue: TNotificationSetting) => void;
  disabled: boolean;
}) => {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  return (
    <HStack width={'100%'} justifyContent={'space-between'}>
      <VStack align={'start'}>
        <Text variant={'urbanistBoldLarge'} color={'text.darkBlue'}>
          {NOTIFICATION_CHANNELS_SETTINGS_TITLE_AND_DESCRIPTION[channel].title}
        </Text>
        <Text variant={'urbanistSemiBold'} color={'text.mediumGray'}>
          {NOTIFICATION_CHANNELS_SETTINGS_TITLE_AND_DESCRIPTION[channel].subtitle}
        </Text>
      </VStack>
      <HStack>
        <RadioButtonGroup
          width={'100px'}
          items={['On', 'Off']}
          disabled={disabled}
          selectedItem={localValue}
          onClick={(newValue: string) => {
            setLocalValue(newValue as TNotificationSetting);
            onChange(channel, newValue as TNotificationSetting);
          }}
        />
      </HStack>
    </HStack>
  );
};
